import type { PageTemplate, BlockType } from "../lib/api";

// PageTemplatePreview renders a small CSS-only silhouette of a page
// template, so the photographer can eyeball what the page will look like
// before picking it — easier than reading the block list as text.
//
// Heights and shading are tuned to read at a glance: hero blocks are
// tall and bright, text blocks are short stripes, image blocks are
// medium gradient swatches, etc.

const BLOCK_STYLE: Record<BlockType, { height: number; bg: string; label?: string }> = {
  hero:        { height: 34, bg: "linear-gradient(135deg,#6366f1,#8b5cf6)", label: "H" },
  full_bleed:  { height: 56, bg: "linear-gradient(135deg,#1e3a8a,#0891b2)" },
  split:       { height: 38, bg: "linear-gradient(90deg,#0891b2 50%, #e2e8f0 50%)" },
  text:        { height: 18, bg: "#94a3b8" },
  image:       { height: 30, bg: "linear-gradient(135deg,#f59e0b,#fbbf24)" },
  quote:       { height: 22, bg: "#a78bfa" },
  callout:     { height: 18, bg: "#ec4899" },
  faq:         { height: 28, bg: "repeating-linear-gradient(180deg,#0ea5e9 0 4px, #38bdf8 4px 8px)" },
  services:    { height: 30, bg: "repeating-linear-gradient(90deg,#14b8a6 0 12px, #5eead4 12px 24px)" },
  gallery_row: { height: 24, bg: "repeating-linear-gradient(90deg,#f97316 0 12px, #fb923c 12px 24px)" },
  divider:     { height: 4,  bg: "#cbd5e1" },
  video:       { height: 30, bg: "linear-gradient(135deg,#ef4444,#dc2626)" },
  statement:   { height: 26, bg: "#312e81" },
  process:     { height: 28, bg: "repeating-linear-gradient(180deg,#0ea5e9 0 6px, #7dd3fc 6px 12px)" },
  gallery_list: { height: 24, bg: "repeating-linear-gradient(90deg,#a855f7 0 12px, #c084fc 12px 24px)" },
  contact_form: { height: 32, bg: "linear-gradient(135deg,#059669,#34d399)" },
  section:      { height: 20, bg: "repeating-linear-gradient(90deg,#64748b 0 8px, #94a3b8 8px 16px)" },
};

export function PageTemplatePreview({ template, height = 130 }: { template: PageTemplate; height?: number }) {
  const blocks = template.Blocks ?? [];

  if (blocks.length === 0) {
    return (
      <div style={{
        height, width: "100%",
        background: "var(--card)",
        border: "1px dashed var(--border)",
        borderRadius: 4,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--muted)", fontSize: "0.75rem",
      }}>Empty page — your blank canvas</div>
    );
  }

  const totalUnits = blocks.reduce((sum, b) => sum + (BLOCK_STYLE[b.BlockType]?.height ?? 20), 0);
  const scale = height / totalUnits;

  return (
    <div style={{
      width: "100%", height,
      background: "linear-gradient(180deg, #fafafa, #f1f5f9)",
      borderRadius: 4,
      overflow: "hidden",
      display: "flex", flexDirection: "column",
      gap: 1,
      border: "1px solid var(--border)",
    }}>
      {blocks.map((b, i) => {
        const meta = BLOCK_STYLE[b.BlockType] ?? { height: 20, bg: "#94a3b8" };
        return (
          <div
            key={i}
            title={b.BlockType}
            style={{
              height: meta.height * scale,
              background: meta.bg,
              flexShrink: 0,
              opacity: 0.92,
            }}
          />
        );
      })}
    </div>
  );
}
